import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Form, Button, Row, Col } from "react-bootstrap";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import SelectDropdown from "../components-react/SelectDropdown";
import FileUploadComponent from "../common-components/FileUploadComponent";
import { selectToken } from "../auth/auth.selector";
import {
  fetchApiData,
  commonPostPutApi,
} from "../../redux/reducers/commonApiSlice";
import useGymsData from "../../hooks/useGymsData";
import "../../styles/ticket-system/fd-dashboard.css";
import { ticketValidationSchema } from "../../schema/schema";

const PRIORITY_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Critical", value: "critical" },
];

const RaiseTicketModal = ({ isOpen, onClose, onSubmit, userInfoData }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  const [uploadedFiles, setUploadedFiles] = useState({
    images: [],
    videos: [],
  });
  const [issueTypes, setIssueTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState({
    issueTypes: false,
    categories: false,
    subcategories: false,
  });
  // Phase 1: when createTicket reports a duplicate, hold the existing-ticket info
  // + the submitted payload so the user can MERGE or raise a RELATED (child) ticket.
  const [dupPrompt, setDupPrompt] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);

  // Fetch gym data using the custom hook
  const {
    data: gymsData,
    isLoading: isLoadingGyms,
    isFetching: isFetchingGyms,
  } = useGymsData();

  // Auto-detect gym based on user info
  const autoDetectedGym = userInfoData?.gym_name
    ? {
        label: userInfoData.gym_name,
        value: userInfoData.gym_id || userInfoData.uid,
      }
    : null;

  const formik = useFormik({
    initialValues: {
      gymName: autoDetectedGym || "",
      ticketRaisedBy: userInfoData?.name || "",
      dateTime: new Date().toLocaleString(),
      issueType: "",
      category: "",
      subcategory: "",
      priority: "",
      description: "",
      recurringIssue: false,
      mediaFiles: { images: [], videos: [] },
    },
    validationSchema: ticketValidationSchema,
    onSubmit: async (values, { setSubmitting, setFieldTouched }) => {
      try {
        setSubmitting(true);

        // Mark mediaFiles as touched to show validation errors
        setFieldTouched("mediaFiles", true);

        // Validate media files before submission
        if (!uploadedFiles.images.length && !uploadedFiles.videos.length) {
          toast.error("At least one image or video is required as proof");
          setSubmitting(false);
          return;
        }

        // Prepare the payload according to API requirements
        const payload = {
          gym_id: values.gymName.value,
          issue_type_id: values.issueType.value,
          category_id: values.category.value,
          sub_category_id: values.subcategory.value,
          priority: values.priority.value,
          description: values.description,
          is_recurring: values.recurringIssue ? 1 : 0,
          created_by: userInfoData?.uid || userInfoData?.user_id,
          status: "opened",
          imgArr: uploadedFiles.images,
          videoArr: uploadedFiles.videos,
        };

        // Call the API to create the ticket
        const result = await dispatch(
          commonPostPutApi({
            method: "POST",
            endpoint: "/ticketSystem/add",
            payload: payload,
            token: token,
          })
        );

        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Ticket created successfully!"
          );

          // Call the parent callback with ticket data if provided
          if (onSubmit && typeof onSubmit === "function") {
            const ticketData = {
              id: result.payload.data?.uid,
              gymName: values.gymName.label,
              gymId: values.gymName.value,
              issueType: values.issueType.label,
              issueTypeId: values.issueType.value,
              category: values.category.label,
              categoryId: values.category.value,
              subcategory: values.subcategory.label,
              subcategoryId: values.subcategory.value,
              priority: values.priority.value,
              status: result.payload.data?.status || "opened",
              description: values.description,
              raisedBy: values.ticketRaisedBy,
              raisedById: userInfoData?.uid || userInfoData?.user_id,
              dateTime:
                result.payload.data?.date_added || new Date().toLocaleString(),
              recurring: values.recurringIssue,
              attachments: [...uploadedFiles.images, ...uploadedFiles.videos],
            };
            onSubmit(ticketData);
          }

          // Reset form and close modal
          formik.resetForm();
          setUploadedFiles({ images: [], videos: [] });
          formik.setFieldValue("mediaFiles", { images: [], videos: [] });
          handleModalClose();
        } else if (
          result?.payload?.already_exist === 1 &&
          (result?.payload?.can_merge || result?.payload?.can_create_child)
        ) {
          // Duplicate found — offer merge / raise-as-related instead of a plain error.
          setDupPrompt({
            existing: result.payload.existing_ticket,
            canMerge: !!result.payload.can_merge,
            canChild: !!result.payload.can_create_child,
            message: result.payload.message,
            payload,
          });
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Failed to create ticket!");
        }
      } catch (error) {
        console.error("Error creating ticket:", error);
        toast.error("An error occurred while creating the ticket!");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch Issue Types
  const fetchIssueTypes = async () => {
    if (!token) return;

    setLoading((prev) => ({ ...prev, issueTypes: true }));
    try {
      const result = await dispatch(
        fetchApiData({
          endpoint: "/ticketSystem/issue-type?limit=100&page=1",
          token: token,
        })
      );

      if (result?.payload?.status) {
        const formattedData =
          result.payload.data
            ?.filter((item) => item.status === "active")
            ?.map((item) => ({
              label: item.name,
              value: item.uid,
            })) || [];
        setIssueTypes(formattedData);
      } else {
        toast.error("Failed to fetch issue types");
      }
    } catch (error) {
      console.error("Error fetching issue types:", error);
      toast.error("Failed to fetch issue types");
    } finally {
      setLoading((prev) => ({ ...prev, issueTypes: false }));
    }
  };

  // Fetch Categories based on Issue Type
  const fetchCategories = async (issueTypeId) => {
    if (!token || !issueTypeId) return;

    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const result = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/category?limit=100&page=1&issue_type_id=${issueTypeId}`,
          token: token,
        })
      );

      if (result?.payload?.status) {
        const formattedData =
          result.payload.data
            ?.filter((item) => item.status === "active")
            ?.map((item) => ({
              label: item.name,
              value: item.uid,
            })) || [];
        setCategories(formattedData);
      } else {
        toast.error("Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  // Fetch Subcategories based on Category
  const fetchSubcategories = async (categoryId) => {
    if (!token || !categoryId) return;

    setLoading((prev) => ({ ...prev, subcategories: true }));
    try {
      const result = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/sub-category?limit=100&page=1&category_id=${categoryId}`,
          token: token,
        })
      );

      if (result?.payload?.status) {
        const formattedData =
          result.payload.data
            ?.filter((item) => item.status === "active")
            ?.map((item) => ({
              label: item.name,
              value: item.uid,
            })) || [];
        setSubcategories(formattedData);
      } else {
        toast.error("Failed to fetch subcategories");
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Failed to fetch subcategories");
    } finally {
      setLoading((prev) => ({ ...prev, subcategories: false }));
    }
  };

  // Load issue types on component mount
  useEffect(() => {
    if (isOpen && token) {
      fetchIssueTypes();
    }
  }, [isOpen, token]);

  const handleDropdownChange = (field, value) => {
    formik.setFieldValue(field, value);

    // Reset dependent dropdowns and fetch data
    if (field === "issueType") {
      formik.setFieldValue("category", "");
      formik.setFieldValue("subcategory", "");
      setCategories([]);
      setSubcategories([]);
      if (value?.value) {
        fetchCategories(value.value);
      }
    } else if (field === "category") {
      formik.setFieldValue("subcategory", "");
      setSubcategories([]);
      if (value?.value) {
        fetchSubcategories(value.value);
      }
    }
  };

  const handleFilesChange = (files) => {
    setUploadedFiles(files);
    // Update formik value for validation
    formik.setFieldValue("mediaFiles", files);
  };

  const handleModalClose = () => {
    formik.resetForm();
    setUploadedFiles({ images: [], videos: [] });
    setIssueTypes([]);
    setCategories([]);
    setSubcategories([]);
    onClose();
  };

  useEffect(() => {
    if (isOpen && gymsData?.length > 0 && userInfoData?.gym_id) {
      const matchingGym = gymsData.find(
        (gym) => gym.value === userInfoData.gym_id
      );

      if (matchingGym) {
        formik.setFieldValue("gymName", matchingGym);
      }
    }

    // Initialize mediaFiles field
    if (isOpen) {
      formik.setFieldValue("mediaFiles", uploadedFiles);
    }
  }, [isOpen, gymsData, userInfoData, formik.setFieldValue, uploadedFiles]);

  const isReceptionist = userInfoData?.ticket_account_type === "receptionist";

  // ---- Phase 1: duplicate merge / add-related handlers ----
  const resetAndCloseDup = () => {
    formik.resetForm();
    setUploadedFiles({ images: [], videos: [] });
    formik.setFieldValue("mediaFiles", { images: [], videos: [] });
    setDupPrompt(null);
    handleModalClose();
  };

  const handleMergeDuplicate = async () => {
    if (!dupPrompt?.existing?.ticket_id) return;
    setDupBusy(true);
    try {
      const res = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: "/ticketSystem/merge",
          payload: {
            ticket_id: dupPrompt.existing.ticket_id,
            merge_description: dupPrompt.payload.description,
            imgArr: dupPrompt.payload.imgArr,
            videoArr: dupPrompt.payload.videoArr,
          },
          token,
        })
      );
      if (res?.payload?.status) {
        toast.success(res.payload.message || "Merged into existing ticket");
        resetAndCloseDup();
      } else {
        toast.error(
          res?.payload?.message || res?.error?.message || "Merge failed"
        );
      }
    } catch (e) {
      toast.error("Merge failed");
    } finally {
      setDupBusy(false);
    }
  };

  const handleAddRelated = async () => {
    if (!dupPrompt?.existing?.ticket_id) return;
    setDupBusy(true);
    try {
      const p = dupPrompt.payload;
      const res = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: "/ticketSystem/add-child",
          payload: {
            parent_ticket_id: dupPrompt.existing.ticket_id,
            gym_id: p.gym_id,
            issue_type_id: p.issue_type_id,
            category_id: p.category_id,
            sub_category_id: p.sub_category_id,
            priority: p.priority,
            description: p.description,
            is_recurring: p.is_recurring,
            created_by: p.created_by,
            imgArr: p.imgArr,
            videoArr: p.videoArr,
          },
          token,
        })
      );
      if (res?.payload?.status) {
        toast.success(res.payload.message || "Related ticket created");
        if (onSubmit && typeof onSubmit === "function") {
          onSubmit({ id: res.payload.ticket_id });
        }
        resetAndCloseDup();
      } else {
        toast.error(
          res?.payload?.message ||
            res?.error?.message ||
            "Failed to create related ticket"
        );
      }
    } catch (e) {
      toast.error("Failed to create related ticket");
    } finally {
      setDupBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModalClose}
      contentLabel="Raise Ticket Modal"
      ariaHideApp={false}
      closeTimeoutMS={300}
    >
      <div className="createNewCTOModalContainer mod-width-50p ticket-system-modal">
        <ModalHeadAndClose
          title="Raise New Facility Ticket"
          onClose={handleModalClose}
        />

        {dupPrompt && (
          <div
            className="ticket-dup-prompt"
            style={{
              padding: "16px",
              border: "1px solid #f0c36d",
              background: "#fff8e1",
              borderRadius: 8,
              margin: "0 0 16px",
            }}
          >
            <p style={{ fontWeight: 600, marginBottom: 8 }}>
              Similar ticket already exists
            </p>
            <p style={{ marginBottom: 6 }}>{dupPrompt.message}</p>
            {dupPrompt.existing && (
              <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                {dupPrompt.existing.ticket_no} ·{" "}
                {dupPrompt.existing.status_label} · by{" "}
                {dupPrompt.existing.raiser_name}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {dupPrompt.canMerge && (
                <button
                  type="button"
                  className="btn-k btn-primary-k small"
                  disabled={dupBusy}
                  onClick={handleMergeDuplicate}
                >
                  {dupBusy ? "Please wait..." : "Merge my report into it"}
                </button>
              )}
              {dupPrompt.canChild && (
                <button
                  type="button"
                  className="btn-k btn-primary-k small"
                  disabled={dupBusy}
                  onClick={handleAddRelated}
                >
                  {dupBusy ? "Please wait..." : "Raise as related ticket"}
                </button>
              )}
              <button
                type="button"
                className="btn-k small"
                disabled={dupBusy}
                onClick={() => setDupPrompt(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <form onSubmit={formik.handleSubmit} className="formContainer mb30">
          <div className="formInputsGrid twoGridSystem">
            <div className="formInputBox">
              <label htmlFor="gymName" className="formInputLabel">
                Gym Name
                {autoDetectedGym && (
                  <span className="auto-detected-label">Auto-Detected</span>
                )}
              </label>
              <div
                className={`commonWrapper ${
                  formik.touched.gymName && formik.errors.gymName
                    ? "formError"
                    : ""
                }`}
              >
                <SelectDropdown
                  options={gymsData || []}
                  selectedOption={formik.values.gymName}
                  setSelectedOption={(selectedValue) =>
                    handleDropdownChange("gymName", selectedValue || "")
                  }
                  name="Gym Name"
                  isDisable={isReceptionist}
                  preventKeyboardOpen={true}
                  isLoading={isLoadingGyms || isFetchingGyms}
                />
              </div>
              {formik.touched.gymName && formik.errors.gymName && (
                <div className="invalid-feedback">{formik.errors.gymName}</div>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="ticketRaisedBy" className="formInputLabel">
                Ticket Raised By
              </label>
              <input
                type="text"
                value={formik.values.ticketRaisedBy}
                readOnly
                className="formInput readonly-field"
              />
            </div>

            <div className="formInputBox">
              <label htmlFor="dateTime" className="formInputLabel">
                Date & Time
              </label>
              <input
                type="text"
                value={formik.values.dateTime}
                readOnly
                className="formInput readonly-field"
              />
            </div>

            <div className="formInputBox">
              <label htmlFor="issueType" className="formInputLabel">
                Issue Type <span className="required">*</span>
              </label>
              <div
                className={`commonWrapper ${
                  formik.touched.issueType && formik.errors.issueType
                    ? "formError"
                    : ""
                }`}
              >
                <SelectDropdown
                  options={issueTypes}
                  selectedOption={formik.values.issueType}
                  setSelectedOption={(selectedValue) =>
                    handleDropdownChange("issueType", selectedValue || "")
                  }
                  name="Issue Type"
                  preventKeyboardOpen={true}
                  isLoading={loading.issueTypes}
                />
              </div>
              {formik.touched.issueType && formik.errors.issueType && (
                <div className="invalid-feedback">
                  {formik.errors.issueType}
                </div>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="category" className="formInputLabel">
                Category <span className="required">*</span>
              </label>
              <div
                className={`commonWrapper ${
                  formik.touched.category && formik.errors.category
                    ? "formError"
                    : ""
                }`}
              >
                <SelectDropdown
                  options={categories}
                  selectedOption={formik.values.category}
                  setSelectedOption={(selectedValue) =>
                    handleDropdownChange("category", selectedValue || "")
                  }
                  name="Category"
                  isDisable={!formik.values.issueType}
                  preventKeyboardOpen={true}
                  isLoading={loading.categories}
                />
              </div>
              {formik.touched.category && formik.errors.category && (
                <div className="invalid-feedback">{formik.errors.category}</div>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="subcategory" className="formInputLabel">
                Subcategory <span className="required">*</span>
              </label>
              <div
                className={`commonWrapper ${
                  formik.touched.subcategory && formik.errors.subcategory
                    ? "formError"
                    : ""
                }`}
              >
                <SelectDropdown
                  options={subcategories}
                  selectedOption={formik.values.subcategory}
                  setSelectedOption={(selectedValue) =>
                    handleDropdownChange("subcategory", selectedValue || "")
                  }
                  name="Subcategory"
                  isDisable={!formik.values.category}
                  preventKeyboardOpen={true}
                  isLoading={loading.subcategories}
                />
              </div>
              {formik.touched.subcategory && formik.errors.subcategory && (
                <div className="invalid-feedback">
                  {formik.errors.subcategory}
                </div>
              )}
            </div>

            <div className="formInputBox">
              <label htmlFor="priority" className="formInputLabel">
                Priority <span className="required">*</span>
              </label>
              <div
                className={`commonWrapper ${
                  formik.touched.priority && formik.errors.priority
                    ? "formError"
                    : ""
                }`}
              >
                <SelectDropdown
                  options={PRIORITY_OPTIONS}
                  selectedOption={formik.values.priority}
                  setSelectedOption={(selectedValue) =>
                    handleDropdownChange("priority", selectedValue || "")
                  }
                  name="Priority"
                  preventKeyboardOpen={true}
                />
              </div>
              {formik.touched.priority && formik.errors.priority && (
                <div className="invalid-feedback">{formik.errors.priority}</div>
              )}
            </div>

            <div className="formInputBox">
              <div className="recurring-checkbox">
                <div>
                  <input
                    type="checkbox"
                    id="recurringIssue"
                    checked={formik.values.recurringIssue}
                    onChange={(e) =>
                      formik.setFieldValue("recurringIssue", e.target.checked)
                    }
                    className="form-check-input"
                  />
                  <label htmlFor="recurringIssue" className="form-check-label">
                    Recurring Issue
                  </label>
                </div>
                <div className="recurring-issue-description">
                  Agar yeh problem baar-baar ho rahi hai (har hafte/mahine) ya
                  pehle bhi iss jagah pe ho chuki hai, toh yahan tick karein.
                </div>
              </div>
            </div>

            <div className="formInputBox span2">
              <label htmlFor="description" className="formInputLabel">
                Description <span className="required">*</span>
                <span className="character-count">
                  ({formik.values.description.length}/250)
                </span>
              </label>
              <textarea
                rows={4}
                maxLength={250}
                placeholder="Describe the issue in detail..."
                {...formik.getFieldProps("description")}
                className={`formInput ${
                  formik.touched.description && formik.errors.description
                    ? "is-invalid"
                    : ""
                }`}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="invalid-feedback">
                  {formik.errors.description}
                </div>
              )}
            </div>
          </div>

          <div className="formInputsGrid uploadsInputGrids">
            <div className="formInputBox span2">
              <FileUploadComponent
                minFiles={1}
                maxFiles={3}
                maxFileSize={50}
                acceptedFormats={["jpg", "jpeg", "png", "mp4"]}
                onFilesChange={handleFilesChange}
                label="Upload Proof (Max 3 files, up to 50MB each) *"
                hint="Upload photos or videos as proof"
                disabled={formik.isSubmitting}
              />
              {formik.touched.mediaFiles && formik.errors.mediaFiles && (
                <div className="invalid-feedback d-block">
                  {formik.errors.mediaFiles}
                </div>
              )}
            </div>

            <div className="formInputBox span2">
              <Button
                type="submit"
                variant="danger"
                className="submit-ticket-btn"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></div>
                    Creating Ticket...
                  </>
                ) : (
                  "Submit Smart Ticket"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RaiseTicketModal;
